import { useRecoilCallback } from 'recoil';
import {
    hoveredAnnotationState,
    removeAnnotation,
    selectedAnnotationState,
    unlessNaming,
} from '~/state/atoms/annotationListAtoms';
import {
    profileHoverState,
    withoutProfileHover,
} from '~/state/atoms/intensityToolAtoms';
import {
    MEASURE_ANNOTATION_LISTS,
    measuredAxialDiametersState,
} from '~/state/atoms/measurementAtoms';

/**
 * Returns a callback that removes one shape of any kind, a measured
 * diameter's ruler included, and the selection, hover and chart pointer
 * naming it; an id nothing holds writes nothing. A structure's measured
 * diameters are forgotten with their last ruler.
 */
export function useRemoveAnnotation(): (id: string) => void {
    return useRecoilCallback(
        (callback) => (id: string) => {
            const { snapshot, set } = callback;
            const list = MEASURE_ANNOTATION_LISTS.find((candidate) =>
                snapshot.getLoadable(candidate.ids).getValue().includes(id)
            );
            const measured = snapshot
                .getLoadable(measuredAxialDiametersState)
                .getValue();
            const removed = (ruler: { readonly id: string }) => ruler.id === id;
            if (list) {
                removeAnnotation(callback, list, id);
            } else if (measured.some(({ rulers }) => rulers.some(removed))) {
                set(
                    measuredAxialDiametersState,
                    measured.flatMap((pair) => {
                        const rulers = pair.rulers.filter((r) => !removed(r));
                        return rulers.length === pair.rulers.length
                            ? [pair]
                            : rulers.length > 0
                              ? [{ ...pair, rulers }]
                              : [];
                    })
                );
            } else {
                return;
            }
            set(selectedAnnotationState, unlessNaming(id));
            set(hoveredAnnotationState, unlessNaming(id));
            set(profileHoverState, withoutProfileHover(id));
        },
        []
    );
}
